<template>
  <teleport to="body">
    <transition name="loading" appear>
      <div 
        v-if="show"
        class="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
        :class="{ 'bg-opacity-75': dark }"
      >
        <div class="flex flex-col items-center space-y-6">
          <!-- BRK Logo Animation -->
          <div class="relative">
            <!-- Outer Ring -->
            <div 
              class="w-24 h-24 border-4 border-transparent rounded-full animate-spin"
              style="border-top-color: #3B82F6; border-right-color: #8B5CF6; animation-duration: 2s;"
            ></div>
            
            <!-- Inner Ring -->
            <div 
              class="absolute inset-2 w-20 h-20 border-4 border-transparent rounded-full animate-spin"
              style="border-bottom-color: #10B981; border-left-color: #F59E0B; animation-duration: 1.5s; animation-direction: reverse;"
            ></div>
            
            <!-- Center BRK Text -->
            <div class="absolute inset-0 flex items-center justify-center">
              <div class="text-center">
                <h2 
                  class="text-2xl font-bold bg-gradient-to-r from-blue-500 via-purple-500 to-green-500 bg-clip-text text-transparent animate-pulse"
                  style="font-family: 'Inter', sans-serif; letter-spacing: 2px;"
                >
                  BRK
                </h2>
              </div>
            </div>
            
            <!-- Floating Particles -->
            <div class="absolute -inset-4">
              <div 
                class="absolute w-2 h-2 bg-blue-400 rounded-full animate-float"
                style="top: 10%; left: 10%; animation-delay: 0s; animation-duration: 3s;"
              ></div>
              <div 
                class="absolute w-1.5 h-1.5 bg-purple-400 rounded-full animate-float"
                style="top: 20%; right: 15%; animation-delay: 1s; animation-duration: 2.5s;"
              ></div>
              <div 
                class="absolute w-2 h-2 bg-green-400 rounded-full animate-float"
                style="bottom: 20%; left: 15%; animation-delay: 0.5s; animation-duration: 2.8s;"
              ></div>
              <div 
                class="absolute w-1 h-1 bg-yellow-400 rounded-full animate-float"
                style="bottom: 10%; right: 10%; animation-delay: 1.5s; animation-duration: 3.2s;"
              ></div>
            </div>
          </div>
          
          <!-- Loading Text -->
          <div class="text-center">
            <h3 
              class="text-xl font-semibold text-white mb-2 animate-pulse"
              :class="{ 'text-gray-800': !dark }"
            >
              {{ title }}
            </h3>
            <p 
              class="text-sm text-gray-300 animate-pulse"
              :class="{ 'text-gray-600': !dark }"
              v-if="message"
            >
              {{ message }}
            </p>
          </div>
          
          <!-- Progress Bar (optional) -->
          <div v-if="showProgress" class="w-64 bg-gray-200 rounded-full h-2 overflow-hidden">
            <div 
              class="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-green-500 rounded-full transition-all duration-500 ease-out animate-progress"
              :style="{ width: `${progress}%` }"
            ></div>
          </div>
          
          <!-- Dots Animation -->
          <div class="flex space-x-2">
            <div 
              v-for="i in 3" 
              :key="i"
              class="w-3 h-3 bg-blue-400 rounded-full animate-bounce"
              :style="{ 'animation-delay': `${(i - 1) * 0.2}s` }"
            ></div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { ref, watch } from 'vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: 'กำลังโหลด...'
  },
  message: {
    type: String,
    default: ''
  },
  dark: {
    type: Boolean,
    default: true
  },
  showProgress: {
    type: Boolean,
    default: false
  },
  progress: {
    type: Number,
    default: 0,
    validator: (value) => value >= 0 && value <= 100
  }
})

// Lock body scroll when loading is shown
watch(() => props.show, (newValue) => {
  if (newValue) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})
</script>

<style scoped>
/* Loading transition */
.loading-enter-active, .loading-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.loading-enter-from, .loading-leave-to {
  opacity: 0;
  backdrop-filter: blur(0px);
}

.loading-enter-to, .loading-leave-from {
  opacity: 1;
  backdrop-filter: blur(4px);
}

/* Custom animations */
@keyframes float {
  0%, 100% {
    transform: translateY(0px) scale(1);
    opacity: 0.7;
  }
  50% {
    transform: translateY(-20px) scale(1.2);
    opacity: 1;
  }
}

@keyframes progress {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(0%);
  }
}

.animate-float {
  animation: float 3s ease-in-out infinite;
}

.animate-progress {
  animation: progress 2s ease-out infinite;
}

/* Gradient text animation */
@keyframes gradient {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.bg-gradient-to-r {
  background-size: 200% 200%;
  animation: gradient 3s ease infinite;
}

/* Responsive design */
@media (max-width: 640px) {
  .w-24 {
    width: 5rem;
  }
  .h-24 {
    height: 5rem;
  }
  .w-20 {
    width: 4.5rem;
  }
  .h-20 {
    height: 4.5rem;
  }
  .text-2xl {
    font-size: 1.5rem;
  }
  .w-64 {
    width: 12rem;
  }
}

/* Enhanced spin animation */
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Bounce animation for dots */
.animate-bounce {
  animation: bounce 1s infinite;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
    animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
  }
  50% {
    transform: translateY(-25%);
    animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
  }
}

/* Pulse animation */
.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: .5;
  }
}
</style>